// ---------------------------------------------------------------------------
// Golden Balls viewer-state types
// ---------------------------------------------------------------------------
// Mirrors packages/game-goldenballs/src/projection.ts. Everything here is
// what the SERVER decided this viewer may see — nulls are redactions, not
// missing data, and the UI must never try to infer around them.

export type GbClaim = number | 'KILLER';

export type GbPhase =
  | 'nomination'
  | 'claim_reveal'
  | 'discussion'
  | 'vote'
  | 'elimination_reveal'
  | 'bin_or_win'
  /** The last talk before Split or Steal. No commit button on screen. */
  | 'pre_final'
  | 'final'
  | 'ended';

export type GbBallView = { ballId: string; kind: 'cash' | 'killer'; value: number };

export type GbBackRowView = {
  ballId: string;
  committed: boolean;
  /** Own claim always; others only after the simultaneous reveal. */
  claim: GbClaim | null;
  /**
   * PUBLIC reveal only — set once a reveal exposed this exact ball to
   * the whole table. Never confuse this with your own knowledge below:
   * showing your own hidden balls as "revealed" reads as a leak.
   */
  revealedValue: GbClaim | null;
  /** Your own ball's true value. Null for every other seat. */
  yourTruth: GbClaim | null;
};

export type GbSeatView = {
  seatId: string;
  label: string;
  alive: boolean;
  eliminatedRound: number | null;
  isYou: boolean;
  /** null while bots are hidden — never render a placeholder for it. */
  isBot: boolean | null;
  votesReceived: number;
  honesty: { verifiedTrue: number; verifiedLied: number };
  timeouts: number;
  frontRow: GbBallView[];
  backRow: GbBackRowView[];
};

export type GbValueGridEntry = {
  value: number;
  /** An inflation-list value rather than a base-reserve one. */
  fromInflationList: boolean;
  confirmedFromR1: boolean;
  /**
   * Round two: this value was face up in round one, so it is back in the
   * pool for CERTAIN. Public knowledge (every round-one ball is turned
   * over at the round-end reveal) — showing it leaks nothing, and hiding
   * it was what made the reserve unreadable in round two.
   */
  returnsFromR1: boolean;
  /**
   * Round two: a candidate for the fresh draw. Only `newDraws` of these
   * entered play and which ones is secret — that is the real unknown.
   */
  couldBeNewDraw: boolean;
  /**
   * Where it is face-up now: front | elimination | win | bin | null.
   * Null covers undrawn, dealt-but-hidden AND the round-two sit-outs —
   * they must stay indistinguishable.
   */
  faceUp: string | null;
  removed: boolean;
};

export type GbBinWinSlot = {
  slot: number;
  placed: boolean;
  revealed: GbClaim | null;
  dest: 'bin' | 'win' | null;
};

export type GbTieBreakStep = { rule: string; detail: string; survivors: string[] };

export type GbVoteReveal = {
  round: 1 | 2;
  votes: Record<string, string>;
  counts: Record<string, number>;
  tiedSeatIds: string[];
  tieBreak?: { preset: string; steps: GbTieBreakStep[] };
  eliminatedSeatId: string;
};

export type GbRevealedBall = {
  ballId: string;
  claimed: GbClaim;
  actual: GbClaim;
  honest: boolean;
};

export type GbSeatReveal = { seatId: string; balls: GbRevealedBall[] };

export type GbElimination = {
  round: 1 | 2;
  seatId: string;
  /** The eliminated seat's balls. A subset of `reveals` — kept for compat. */
  balls: GbRevealedBall[];
  /**
   * EVERY seat's hidden balls, turned over together. All the balls go
   * back into the pool between rounds, so there is nothing left to
   * protect — and this is the honesty read on the players still in.
   */
  reveals: GbSeatReveal[];
};

export type GbChatEntry = {
  seq: number;
  round: 1 | 2;
  phase: GbPhase;
  seatId: string;
  phraseId: string;
  params: Record<string, string | number>;
  text: string;
};

export type GbPlacement = {
  index: number;
  seatId: string;
  ballId: string;
  dest: 'bin' | 'win';
  revealed: GbClaim;
  totalAfter: number;
  forced: boolean;
  byTimeout: boolean;
};

export type GbPoolEntry = {
  ballId: string;
  ownerSeatId: string | null;
  source: 'front' | 'back' | 'extra';
  /** Face-up identity (front-row balls and the extra killer). */
  faceUp: GbClaim | null;
  /** The owner's round-two claim for a hidden ball. */
  claim: GbClaim | null;
  /** Your own hidden ball's true value — you always know your own. */
  yourTruth: GbClaim | null;
};

export type GbFinalChoice = 'split' | 'steal' | 'sting';

export type GbSeatResult = {
  seatId: string;
  label: string;
  isBot: boolean;
  reachedFinal: boolean;
  eliminatedRound?: 1 | 2;
  votesReceived: number;
  finalChoice?: GbFinalChoice;
  finalByTimeout: boolean;
  payout: number;
  claimsTrue: number;
  claimsLied: number;
  intentStated: number;
  intentMatched: number;
  timeouts: number;
};

export type GbDebrief = {
  jackpot: number;
  finalResult?: {
    choices: Record<string, GbFinalChoice>;
    byTimeout: string[];
    payouts: Record<string, number>;
    destroyed: number;
    explanation: string;
  };
  claimAudit: Array<{
    round: 1 | 2;
    seatId: string;
    ballId: string;
    claimed: GbClaim;
    actual: GbClaim;
    honest: boolean;
    verifiedAt?: 'elimination' | 'binwin';
  }>;
  bots: Array<{ seatId: string; finalIntent: 'SPLIT' | 'STEAL' | 'ADAPTIVE' }>;
  identities: Record<string, string>;
  finalAnalysis?: Record<
    string,
    { chosen: GbFinalChoice; payoutByChoice: Record<GbFinalChoice, number>; optimal: boolean }
  >;
  perSeat: GbSeatResult[];
};

export type GoldenBallsViewerState = {
  gameKey: 'golden-balls';
  phase: GbPhase;
  round: 1 | 2;
  config: {
    ballMode: 'twoBall' | 'threeBall';
    inflationSetting: string;
    anonymityMode: 'hidden' | 'lateReveal' | 'openBook' | 'tiered';
    tieBreakPreset: string;
    botsRevealed: boolean;
    freeChatEnabled: boolean;
    stingReward: number;
    redistributeRemainder: boolean;
    timers: Record<string, number>;
  };
  seats: GbSeatView[];
  valueGrid: {
    cashValues: GbValueGridEntry[];
    killers: { inPool: number; faceUpFront: number };
    /** Round two: how many fresh values were drawn from the leftovers. */
    newDraws: number;
    /** …out of how many candidates. The only genuine unknown in round two. */
    newDrawCandidates: number;
  };
  legalClaimValues: GbClaim[];
  nomination: { committedSeats: Record<string, boolean> } | null;
  vote: { votedSeats: Record<string, boolean>; yourVote: string | null } | null;
  voteReveals: GbVoteReveal[];
  eliminations: GbElimination[];
  chatLog: GbChatEntry[];
  /** Things aimed at YOU that you can answer right now. */
  inbox: {
    pactOffers: Array<{ fromSeatId: string; conditional: boolean; text: string }>;
    accusers: string[];
  };
  identities: Record<string, string>;
  you: { seatId: string; label: string } | null;
  /** Skip / fast forward: who's ready, and who the phase is waiting on. */
  skip: { readySeatIds: string[]; waitingOn: string[]; youAreReady: boolean };
  binwin?: {
    order: [string, string];
    currentPlacer: string | null;
    runningTotal: number;
    binCapacity: number;
    winRow: Array<{ ballId: string; revealed: GbClaim | null }>;
    binRow: Array<{ ballId: string; revealed: GbClaim | null }>;
    placements: GbPlacement[];
    /** What went in — public. You just don't know which slot holds what. */
    composition: { cashValues: number[]; killers: number };
    /** Shuffled, blind slots. Values appear only as they are placed. */
    slots: GbBinWinSlot[];
    /** Finalists who asked to fast-forward. Both must, before it happens. */
    skipToEndSeatIds: string[];
  };
  final?: {
    jackpot: number;
    committedSeats: Record<string, boolean>;
    yourChoice: GbFinalChoice | null;
    result: GbDebrief['finalResult'] | null;
  };
  debrief?: GbDebrief;
};

/** Duck-type guard for MatchPage's viewer-state branch. */
export function isGoldenBallsView(view: unknown): view is GoldenBallsViewerState {
  return (
    !!view &&
    typeof view === 'object' &&
    (view as { gameKey?: unknown }).gameKey === 'golden-balls'
  );
}

/** Play money: dollars, thousands separators, no cents. */
export function fmtMoney(value: number): string {
  return `$${Math.floor(value).toLocaleString('en-US')}`;
}

export function fmtClaim(claim: GbClaim | null): string {
  if (claim === null) return '—';
  return claim === 'KILLER' ? 'KILLER' : fmtMoney(claim);
}

/**
 * "1 lie", "2 lies" — never "1 lies". Use this for EVERY counted noun
 * shown to a player; hand-written `${n} things` is how the singular
 * bug gets back in.
 */
export function plural(count: number, singular: string, pluralForm?: string): string {
  return count === 1 ? singular : (pluralForm ?? `${singular}s`);
}

/** The count and its correctly-pluralised noun: "1 lie", "3 lies". */
export function countOf(count: number, singular: string, pluralForm?: string): string {
  return `${count} ${plural(count, singular, pluralForm)}`;
}
